import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "components/translations/useTranslation";
import { useSelector, useDispatch } from "react-redux";
import Comment from "components/input/Comment.component";
import OKCancelButtons from "components/modals/OKCancelButtons";
import { MODAL_CONTENT_TAB_INDEX } from "utils/constants";
import { ApproveProps } from "types/modals";
import { Checkbox } from "components/common/checkbox.component";
import DisplayProgress from "components/common/displayProgress";
import { CheckUnevaluatedLines, CheckDraftComment } from "components/popup/ApprovePopupValidations";
import * as reduxSelectors from "components/store/application.reducers";
import { approveMultipleTasks } from "components/mytasks/action/approveMultipleTasks.action.js";
import { fetchDraftComment, updateUserSettings } from "components/modals/modal.actions";
import { checkForUnevaluatedLines } from "components/mytasks/action/taskDetailsCheck";
import { Store } from "redux";
import { TaskData } from "types/task";

const Approve = ({ more, closeModal, loopFocus, initialFocusRef }: ApproveProps): React.ReactElement => {
    const { translate } = useTranslation();
    const dispatch = useDispatch();
    const userSettings = useSelector((store: Store) => reduxSelectors.getUsersSettings(store));
    const hasDraftComment = useSelector((store: Store) => reduxSelectors.getTaskDraftCommentText(store));
    const actionParameters = useSelector((store: Store) => reduxSelectors.getActionParameters(store));
    const context = useSelector((store: Store) => reduxSelectors.getUsersCurrentContextId(store));

    const [comment, setComment] = useState("");
    const [hideDialog, setHideDialog] = useState(false);
    const [actionRunning, setActionRunning] = useState(false);
    const [hasUnevaluatedLines, setHasUnevaluatedLines] = useState(false);
    const [hasTaskWithDraftComment, setHasTaskWithDraftComment] = useState(false);
    const [showProgress, setShowProgress] = useState(false);

    const tasks = more?.tasks ?? [];
    const showComments = more?.showComments ?? false;
    const taskDetails = more?.taskDetails;

    useEffect(() => {
        // Check for unevaluated lines
        if (taskDetails?.taskPermission?.hasLinesNotEvaluated) {
            setHasUnevaluatedLines(true);
        } else {
            setShowProgress(true);
            const tasksWithAccountingGrid = (Object.values(actionParameters?.tasksData || {}) as TaskData[]).filter(
                (task) => task.hasAccountingGrid
            );

            checkForUnevaluatedLines(tasksWithAccountingGrid)
                .then((result) => {
                    setHasUnevaluatedLines(result);
                    setShowProgress(false);
                })
                .catch(() => {
                    setHasUnevaluatedLines(false);
                    setShowProgress(false);
                });
        }

        // Check for draft comments
        const findDraftComment = (taskList: Array<Record<string, any>>) => {
            return taskList.some((task) => task.hasDraftComment === true);
        };

        if (findDraftComment(tasks) || hasDraftComment) {
            setHasTaskWithDraftComment(true);
        }

        // Fetch draft comment from API for single task with comments, matching legacy PopUp behavior
        dispatch(fetchDraftComment(showComments, tasks, setComment));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally run only on mount, matching original componentDidMount behavior

    const handleApprove = useCallback(() => {
        setActionRunning(true);

        if (hideDialog) {
            const newData = { ...userSettings, showCommentOnApprove: false };
            dispatch(updateUserSettings(newData, context));
        }

        const actionParams = actionParameters;
        dispatch(approveMultipleTasks(actionParams?.tasksData, comment, undefined, undefined, false, more?.navigate));
        closeModal();
    }, [hideDialog, userSettings, context, actionParameters, comment, more?.navigate, dispatch, closeModal]);

    const handleHideDialogChange = useCallback(() => {
        setHideDialog((prev) => !prev);
    }, []);

    const okEnabled = !actionRunning;

    return (
        <>
            <div className="ga-modal__content">
                {showComments ? (
                    <div>
                        <Comment
                            ref={initialFocusRef}
                            inputHint={translate("popUp.approve.commentHint")}
                            translate={translate}
                            shouldFocusFirst={true}
                            tabIndex={MODAL_CONTENT_TAB_INDEX}
                            id="comment"
                            value={comment}
                            propagateValue={setComment}
                            label={translate("popUp.approve.commentLabel")}
                        />
                        <div className={`my-3 ${(hasUnevaluatedLines || hasTaskWithDraftComment) ? "disabled" : ""}`}>
                            <Checkbox
                                inputId="hideApproveDialog"
                                label={translate("popUp.approve.hideDialog")}
                                isChecked={hideDialog}
                                isDisabled={hasUnevaluatedLines || hasTaskWithDraftComment}
                                onChange={handleHideDialogChange}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="mb-4">
                        {translate("popUp.approve.approveNTasks", tasks.length)}
                    </div>
                )}

                {hasTaskWithDraftComment && (
                    <CheckDraftComment
                        numberOfTasks={tasks.length}
                        showComments={showComments}
                        translate={translate}
                    />
                )}

                {showProgress && (
                    <DisplayProgress message={translate("popUp.approve.checkingForUnevaluatedLines")} />
                )}

                {hasUnevaluatedLines && (
                    <CheckUnevaluatedLines
                        mayHaveUnevaluatedLines={hasUnevaluatedLines}
                        hasUnevaluatedLines={hasUnevaluatedLines}
                        translate={translate}
                    />
                )}
            </div>
            <OKCancelButtons
                tabIndex={MODAL_CONTENT_TAB_INDEX + 1}
                okEnabled={okEnabled}
                okCallback={handleApprove}
                okText={translate("popUp.ok")}
                cancelEnabled={true}
                cancelCallback={closeModal}
                cancelText={translate("popUp.cancel")}
                loopFocus={loopFocus}
                okRef={showComments ? undefined : initialFocusRef}
            />
        </>
    );
};

export default Approve;
